import { useCallback, useEffect, useMemo } from 'react';

import { APIProvider, Map } from '@vis.gl/react-google-maps';

import Button from '#globalComponents/Button/Button';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';

import AddToLeadsModal from '../../components/AddToLeadsModal/AddToLeadsModal';
import { B2B_CATEGORIES } from '../../components/CategoryDropdown/categories';
import CategoryDropdown from '../../components/CategoryDropdown/CategoryDropdown';
import ClusteredMarker from '../../components/ClusteredMarker/ClusteredMarker';
import MarkerClusterer from '../../components/MarkerClusterer/MarkerClusterer';
import PlaceInfoWindow from '../../components/PlaceInfoWindow/PlaceInfoWindow';
import PlacesList from '../../components/PlacesList/PlacesList';
import SearchBar from '../../components/SearchBar/SearchBar';
import { usePlacesByCategory, usePlacesByName } from '../../models/queries';
import { useMapSearchStore } from '../../stores/mapSearchStore';

/**
 * Extracts a plain bounds object from a Google Maps event or a map instance.
 * Returns null if bounds cannot be determined.
 */
function extractBounds(event, mapInstance) {
	const eventBounds = event?.detail?.bounds;

	if (eventBounds) {
		if (typeof eventBounds.north === 'number') {
			return {
				north: eventBounds.north,
				south: eventBounds.south,
				east: eventBounds.east,
				west: eventBounds.west,
			};
		}
		if (eventBounds.getNorthEast && typeof eventBounds.getNorthEast === 'function') {
			return {
				north: eventBounds.getNorthEast().lat(),
				south: eventBounds.getSouthWest().lat(),
				east: eventBounds.getNorthEast().lng(),
				west: eventBounds.getSouthWest().lng(),
			};
		}
	}

	if (mapInstance) {
		try {
			const bounds = mapInstance.getBounds();
			if (bounds && bounds.getNorthEast && typeof bounds.getNorthEast === 'function') {
				return {
					north: bounds.getNorthEast().lat(),
					south: bounds.getSouthWest().lat(),
					east: bounds.getNorthEast().lng(),
					west: bounds.getSouthWest().lng(),
				};
			}
		} catch {
			// ignored
		}
	}

	return null;
}

const MAP_STYLES = [
	{ featureType: 'administrative', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
	{ featureType: 'poi', stylers: [{ visibility: 'off' }] },
	{ featureType: 'road', stylers: [{ visibility: 'off' }] },
	{ featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
	{ featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

const MapSearchPage = () => {
	const {
		mapCenter,
		mapZoom,
		mapBounds,
		mapInstance,
		selectedCategories,
		searchQuery,
		activeSearchMode,
		selectedPlace,
		selectedPlaces,
		isModalOpen,
		sortBy,
		setMapCenter,
		setMapZoom,
		setMapBounds,
		setMapInstance,
		handleCategoryChange,
		handleSearchChange,
		handleSearchClear,
		handleMarkerClick,
		handleInfoWindowClose,
		handlePlaceSelectionToggle,
		handleSelectAll,
		openModal,
		closeModal,
		setSortBy,
	} = useMapSearchStore();

	// Determine which query to use based on active search mode
	const hasCategories = selectedCategories.length > 0;
	const shouldFetchByCategory = activeSearchMode === 'category' && hasCategories && mapBounds && mapZoom >= 13;
	const shouldFetchByName = activeSearchMode === 'name' && searchQuery?.length >= 2 && mapBounds;

	// Get types array from selected category
	const categoryTypes = useMemo(() => {
		if (!shouldFetchByCategory || !hasCategories) return null;
		const category = B2B_CATEGORIES.find((cat) => cat.value === selectedCategories[0]);
		return category?.types || null;
	}, [shouldFetchByCategory, hasCategories, selectedCategories]);

	// Fetch places by category (pass the types array)
	const {
		data: categoryData,
		isLoading: isCategoryLoading,
		error: categoryError,
	} = usePlacesByCategory(categoryTypes, shouldFetchByCategory ? mapBounds : null, mapZoom);

	// Fetch places by name
	const {
		data: nameData,
		isLoading: isNameLoading,
		error: nameError,
	} = usePlacesByName(shouldFetchByName ? searchQuery : null, shouldFetchByName ? mapBounds : null);

	// Determine which data to display and normalize the data structure
	const places = useMemo(() => {
		let data = [];
		if (activeSearchMode === 'name' && shouldFetchByName) {
			data = nameData || [];
		} else if (activeSearchMode === 'category' && shouldFetchByCategory) {
			data = categoryData || [];
		}

		return data.map((place) => ({
			...place,
			position: place.location || place.position || { lat: 0, lng: 0 },
		}));
	}, [activeSearchMode, shouldFetchByName, shouldFetchByCategory, nameData, categoryData]);

	const isLoading = isCategoryLoading || isNameLoading;
	const error = categoryError || nameError;

	// Handle map bounds change
	const handleBoundsChanged = useCallback(
		(event) => {
			const boundsObj = extractBounds(event, mapInstance);
			if (boundsObj) setMapBounds(boundsObj);

			if (mapInstance) {
				try {
					const center = mapInstance.getCenter();
					if (center) setMapCenter({ lat: center.lat(), lng: center.lng() });

					const zoom = mapInstance.getZoom();
					if (zoom) setMapZoom(zoom);
				} catch {
					// ignored
				}
			}
		},
		[mapInstance, setMapBounds, setMapCenter, setMapZoom],
	);

	const handleMapLoad = useCallback(
		(map) => {
			setMapInstance(map);
			setTimeout(() => {
				const boundsObj = extractBounds(null, map);
				if (boundsObj) setMapBounds(boundsObj);
			}, 200);
		},
		[setMapInstance, setMapBounds],
	);

	// Update bounds when map loads
	useEffect(() => {
		if (mapInstance) {
			const timer = setTimeout(() => handleBoundsChanged(), 100);
			return () => clearTimeout(timer);
		}
	}, [mapInstance, handleBoundsChanged]);

	return (
		<Page width="full" scrollMode="content" header={<PageHeader breadcrumbs={['Kartensuche']} />}>
			<div className="flex gap-4 flex-1 min-h-0">
				{/* Map Container - 2/3 width */}
				<div className="relative flex-1 w-2/3">
					{/* Search Controls Overlay */}
					<div className="absolute top-4 left-4 right-4 z-10 flex flex-col sm:flex-row gap-3">
						<div className="flex-1 max-w-md">
							<SearchBar
								value={searchQuery}
								onChange={handleSearchChange}
								onClear={handleSearchClear}
								placeholder="Begriff, Branchen oder Unternehmen suchen..."
							/>
						</div>
						<div className="w-full sm:w-64">
							<CategoryDropdown value={selectedCategories} onChange={handleCategoryChange} />
						</div>
					</div>

					{/* Info Banner */}
					{mapZoom < 13 && hasCategories && !searchQuery && (
						<div className="absolute top-20 left-4 right-4 z-10 bg-yellow-50 border border-yellow-200 rounded-lg p-4 shadow-md max-w-md mx-auto">
							<p className="text-sm text-caution-700 text-center">
								Zoomen Sie auf mindestens Level 13, um Standorte anzuzeigen
							</p>
						</div>
					)}

					{/* Loading Indicator */}
					{isLoading && (
						<div className="absolute top-20 right-4 z-10 bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-md">
							<div className="flex items-center gap-2">
								<div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
								<span className="text-sm text-text">Lädt...</span>
							</div>
						</div>
					)}

					{/* Error Message */}
					{error && (
						<div className="absolute top-20 left-4 right-4 z-10 bg-red-50 border border-red-200 rounded-lg p-4 shadow-md max-w-md mx-auto">
							<p className="text-sm text-warning-700 text-center">
								Fehler beim Laden der Daten. Bitte versuchen Sie es erneut.
							</p>
						</div>
					)}

					{/* Google Map */}
					<APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
						<Map
							defaultCenter={mapCenter}
							defaultZoom={mapZoom}
							gestureHandling="greedy"
							disableDefaultUI={true}
							className="w-full h-full rounded-lg shadow-sm bg-white border border-gray-200"
							onBoundsChanged={handleBoundsChanged}
							onCameraChanged={handleBoundsChanged}
							onLoad={handleMapLoad}
							styles={MAP_STYLES}
						>
							<MarkerClusterer>
								{places.map((place) => {
									const placeId = place.placeId || place.place_id;
									const isInSelectedPlaces = selectedPlaces.some(
										(p) => (p.placeId || p.place_id) === placeId,
									);

									return (
										<ClusteredMarker
											key={placeId}
											position={place.position}
											onClick={() => handleMarkerClick(place)}
											isSelected={isInSelectedPlaces}
										/>
									);
								})}
							</MarkerClusterer>

							{selectedPlace && <PlaceInfoWindow place={selectedPlace} onClose={handleInfoWindowClose} />}
						</Map>
					</APIProvider>
				</div>

				{/* Sidebar - 1/3 width */}
				<div className="w-1/3 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden flex flex-col">
					<PlacesList
						places={places}
						selectedPlaces={selectedPlaces}
						onPlaceClick={handleMarkerClick}
						onPlaceSelectionToggle={handlePlaceSelectionToggle}
						onSelectAll={() => handleSelectAll(places)}
						sortBy={sortBy}
						onSortChange={setSortBy}
					/>

					{/* Bottom Action Bar */}
					{selectedPlaces.length > 0 && (
						<div className="p-4 border-t bg-gray-50">
							<p className="text-sm font-medium mb-3">{selectedPlaces.length} Unternehmen ausgewählt</p>
							<div className="space-y-2">
								<Button onClick={openModal} fullWidth>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="24"
										height="24"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
										className="h-4 w-4"
									>
										<path d="M5 12h14"></path>
										<path d="M12 5v14"></path>
									</svg>
									Zu Leads hinzufügen
								</Button>
							</div>
						</div>
					)}
				</div>
			</div>

			<AddToLeadsModal isOpen={isModalOpen} onClose={closeModal} selectedPlaces={selectedPlaces} />
		</Page>
	);
};

export default MapSearchPage;
